import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { Sparkles, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { formatDate } from "@/lib/utils-cart";

const TASKS = [
  ["product_description", "Product description"],
  ["seo_article", "SEO article"],
  ["ad_copy", "Ad copy"],
  ["email_copy", "Email copy"],
  ["instagram_caption", "Instagram caption"],
  ["tiktok_caption", "TikTok caption"],
  ["facebook_post", "Facebook post"],
  ["landing_page", "Landing page copy"],
];

export default function AIMarketing() {
  const [form, setForm] = useState({
    task_type: "product_description",
    product_title: "",
    product_features: "",
    keywords: "",
    tone: "professional and persuasive",
    brand_voice: "",
    word_count: 200,
  });
  const [history, setHistory] = useState([]);
  const [output, setOutput] = useState("");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => { api.get("/marketing/ai/history").then((r) => setHistory(r.data)); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setOutput("");
    try {
      const { data } = await api.post("/marketing/ai/generate", {
        ...form,
        product_features: form.product_features.split("\n").map(s => s.trim()).filter(Boolean),
        keywords: form.keywords.split(",").map(s => s.trim()).filter(Boolean),
        word_count: Number(form.word_count),
      });
      setOutput(data.content);
      api.get("/marketing/ai/history").then((r) => setHistory(r.data));
    } catch (e) {
      toast.error("Generation failed — try again");
    } finally {
      setLoading(false);
    }
  };

  const copy = () => {
    navigator.clipboard.writeText(output);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <DashboardLayout title="AI Marketing" subtitle="Powered by Claude Sonnet 4.5. Generate copy you'd actually publish.">
      <div className="grid lg:grid-cols-3 gap-4">
        <form onSubmit={submit} className="v-card space-y-3 lg:col-span-1">
          <div>
            <label className="label-eyebrow mb-2 block">What are we writing?</label>
            <select className="v-input" value={form.task_type} onChange={(e) => setForm({...form, task_type: e.target.value})} data-testid="ai-task-type">
              {TASKS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <Field label="Product title" v={form.product_title} on={(v) => setForm({...form, product_title: v})} testid="ai-product-title" required />
          <div>
            <label className="label-eyebrow mb-2 block">Features (one per line)</label>
            <textarea rows={4} className="v-input" value={form.product_features} onChange={(e) => setForm({...form, product_features: e.target.value})} data-testid="ai-features" />
          </div>
          <Field label="SEO keywords (comma)" v={form.keywords} on={(v) => setForm({...form, keywords: v})} testid="ai-keywords" />
          <Field label="Tone" v={form.tone} on={(v) => setForm({...form, tone: v})} testid="ai-tone" />
          <Field label="Brand voice (optional)" v={form.brand_voice} on={(v) => setForm({...form, brand_voice: v})} testid="ai-brand-voice" />
          <Field label="Target word count" type="number" v={form.word_count} on={(v) => setForm({...form, word_count: v})} testid="ai-wordcount" />
          <button disabled={loading} className="v-btn-primary w-full" data-testid="ai-generate">
            <Sparkles size={14} /> {loading ? "Generating…" : "Generate"}
          </button>
        </form>

        <div className="lg:col-span-2 v-card">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-lg font-semibold text-primary">Output</h3>
            {output && (
              <button onClick={copy} className="v-btn-outline text-xs" data-testid="ai-copy">
                {copied ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
              </button>
            )}
          </div>
          <div className="mt-4 min-h-[300px] border border-border rounded-sm p-4 bg-muted/30 font-sans whitespace-pre-wrap text-sm leading-relaxed" data-testid="ai-output">
            {loading ? <div className="text-muted-foreground">Claude is thinking…</div> : output || <div className="text-muted-foreground">Your generated copy will appear here.</div>}
          </div>
        </div>
      </div>

      {history.length > 0 && (
        <div className="mt-6 v-card">
          <h3 className="font-display text-lg font-semibold text-primary">Recent generations</h3>
          <div className="mt-4 space-y-2">
            {history.map((h) => (
              <div key={h.id} className="border border-border rounded-sm p-3">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-medium text-primary">{h.input?.product_title} <span className="text-xs text-muted-foreground font-mono ml-2">{h.task_type}</span></div>
                  <span className="text-xs text-muted-foreground font-mono">{formatDate(h.created_at)}</span>
                </div>
                <div className="mt-1 text-xs text-muted-foreground line-clamp-2">{h.content}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}

function Field({ label, type = "text", v, on, testid, required }) {
  return (
    <div>
      <label className="label-eyebrow mb-2 block">{label}</label>
      <input type={type} className="v-input" value={v} onChange={(e) => on(e.target.value)} required={required} data-testid={testid} />
    </div>
  );
}
